import { useCallback, useMemo, useState } from "react";
import {
  BlockStack,
  Modal,
  Text,
  DataTable,
} from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Code, Copy, Check, Eye, EyeOff } from "lucide-react";
import useIsPhone from "features/files/use-is-phone";
import SensitiveAmount from "components/sensitive-amount/SensitiveAmount";
import { SensitivePersonalCode } from "components/sensitive-amount/SensitiveText";

// Field names whose values are hidden behind an eye click in the history:
// salaries (shown as money once revealed) and the asmens kodas.
const SALARY_KEYS = ["salary", "salaryAfterChange", "newSalary", "currentSalary"];
const PERSONAL_CODE_KEY = "personalCode";
const SENSITIVE_KEYS = [...SALARY_KEYS, PERSONAL_CODE_KEY];
const MASKED_VALUE = "••••";

function normalizeChanges(changes: unknown): { parsed: unknown; raw: string } {
  if (changes == null) {
    return { parsed: null, raw: "" };
  }
  if (typeof changes === "string") {
    if (changes.trim() === "") {
      return { parsed: null, raw: "" };
    }
    try {
      const p = JSON.parse(changes) as unknown;
      return { parsed: p, raw: JSON.stringify(p, null, 2) };
    } catch {
      return { parsed: null, raw: changes };
    }
  }
  try {
    return {
      parsed: changes,
      raw: JSON.stringify(changes, null, 2),
    };
  } catch {
    return { parsed: changes, raw: String(changes) };
  }
}

function isObjectValue(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** A copy of the payload with every sensitive value replaced by «••••», at any depth. */
function maskSensitiveValues(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(maskSensitiveValues);
  if (!isObjectValue(value)) return value;
  return Object.fromEntries(
    Object.entries(value).map(([key, child]) => [
      key,
      SENSITIVE_KEYS.includes(key) && child != null ? MASKED_VALUE : maskSensitiveValues(child),
    ])
  );
}

/**
 * Payload text that is not valid JSON (the server cuts long payloads) cannot be
 * walked as an object, so sensitive values are masked by key name instead.
 */
const SENSITIVE_IN_TEXT = new RegExp(`(\\b(?:${SENSITIVE_KEYS.join("|")})"?\\s*:\\s*)(?!null\\b)[^,}\\]\\s]+`, "g");

function maskSensitiveText(text: string): string {
  return text.replace(SENSITIVE_IN_TEXT, `$1"${MASKED_VALUE}"`);
}

function diffText(value: unknown): string {
  return typeof value === "object" && value !== null ? JSON.stringify(value) : String(value);
}

/** A before/after value in the diff table; a sensitive value gets its own eye, a nested object has its sensitive values masked. */
function diffCell(key: string, value: unknown) {
  if (SALARY_KEYS.includes(key)) return <SensitiveAmount value={diffText(value)} />;
  // A cleared code (null) shows the dash, not a masked "null".
  if (key === PERSONAL_CODE_KEY) return <SensitivePersonalCode value={value == null ? null : String(value)} />;
  return diffText(maskSensitiveValues(value));
}

type DiffRow = { key: string; before: unknown; after: unknown };

const SIDE_STYLES = {
  before: { box: "bg-red-50/50 text-red-700 border-red-100/50", dash: "text-red-400/80" },
  after: { box: "bg-emerald-50/50 text-emerald-700 border-emerald-100/50", dash: "text-emerald-400/80" },
};

/** The old or the new value of one changed field; a value that was absent or empty shows a dash. */
function ChangedValue({ row, side, className = "" }: { row: DiffRow; side: "before" | "after"; className?: string }) {
  const value = row[side];
  const styles = SIDE_STYLES[side];
  return (
    <div className={`px-2 py-1.5 rounded-lg border text-xs font-mono break-all whitespace-pre-wrap ${styles.box} ${className}`}>
      {value === undefined || value === "" ? (
        <span className={`${styles.dash} italic`}>—</span>
      ) : (
        diffCell(row.key, value)
      )}
    </div>
  );
}

function buildSummary(
  t: (k: string, o?: Record<string, unknown>) => string,
  parsed: unknown
): string {
  if (parsed == null) {
    return t("audit_changes_empty", { defaultValue: "No field changes" });
  }
  if (Array.isArray(parsed)) {
    return t("audit_changes_array_n", {
      count: parsed.length,
      defaultValue: "List with {{count}} item(s)",
    });
  }
  if (typeof parsed === "object") {
    const keys = Object.keys(parsed as object);
    if (keys.length === 0) {
      return t("audit_changes_empty", { defaultValue: "No field changes" });
    }
    const top = keys.slice(0, 3).join(", ");
    if (keys.length <= 3) {
      return t("audit_changes_fields", {
        keys: top,
        defaultValue: "Fields: {{keys}}",
      });
    }
    return t("audit_changes_fields_n", {
      count: keys.length,
      sample: top,
      defaultValue: "{{count}} fields, including: {{sample}}",
    });
  }
  if (typeof parsed === "string" && parsed.length > 200) {
    return `${parsed.slice(0, 200)}…`;
  }
  return String(parsed);
}

type Props = {
  changes: unknown;
};

export function hasAuditChangePayload(changes: unknown): boolean {
  return Boolean(normalizeChanges(changes).raw);
}

export function AuditChangesTrigger({ changes }: Props) {
  const { t } = useTranslation();
  const isPhone = useIsPhone();
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showHiddenValues, setShowHiddenValues] = useState(false);
  const { parsed, raw } = useMemo(
    () => normalizeChanges(changes),
    [changes]
  );
  // The payload as text (raw JSON view and «Copy JSON»): sensitive values stay «••••» until «show hidden values» is clicked.
  // The masked text differs from the raw one only when the payload holds a sensitive value.
  const maskedRaw = useMemo(
    () => (isObjectValue(parsed) || Array.isArray(parsed) ? JSON.stringify(maskSensitiveValues(parsed), null, 2) : maskSensitiveText(raw)),
    [parsed, raw]
  );
  const hasHiddenValues = maskedRaw !== raw;
  const shownRaw = hasHiddenValues && !showHiddenValues ? maskedRaw : raw;

  const isBeforeAfterFormat = (() => {
    if (!isObjectValue(parsed)) return false;

    const hasBefore = "before" in parsed;
    const hasAfter = "after" in parsed;
    if (!hasBefore && !hasAfter) return false;

    return (
      (!hasBefore || parsed.before == null || isObjectValue(parsed.before)) &&
      (!hasAfter || parsed.after == null || isObjectValue(parsed.after))
    );
  })();

  const diffRows = useMemo(() => {
    if (!isBeforeAfterFormat) return null;
    const p = parsed as any;
    const before = p.before ?? {};
    const after = p.after ?? {};
    
    // Ensure before and after are objects
    const bObj = typeof before === "object" && before !== null ? before : {};
    const aObj = typeof after === "object" && after !== null ? after : {};
    
    const allKeys = Array.from(new Set([...Object.keys(bObj), ...Object.keys(aObj)]));
    
    const rows: DiffRow[] = [];
    allKeys.forEach((key) => {
      const bRaw = bObj[key];
      const aRaw = aObj[key];
      
      const bStr = bRaw === undefined ? "" : diffText(bRaw);
      const aStr = aRaw === undefined ? "" : diffText(aRaw);
      
      if (bStr !== aStr) {
        // In partial updates, fields that didn't change are omitted from the 'after' object.
        // We skip showing them as deleted.
        if (!(key in aObj) && Object.keys(aObj).length > 0) {
          return;
        }

        rows.push({ key, before: bRaw, after: aRaw });
      }
    });
    return rows;
  }, [parsed, isBeforeAfterFormat]);

  const summary = useMemo(
    () => (raw ? buildSummary(t, parsed) : null),
    [t, parsed, raw]
  );

  const onCopy = useCallback(() => {
    if (!raw) {
      return;
    }
    void navigator.clipboard.writeText(shownRaw).then(
      () => {
        setCopied(true);
        toast.success(t("copied", { defaultValue: "Copied to clipboard" }));
        setTimeout(() => setCopied(false), 2000);
      },
      () => {
        toast.error(t("copy_failed", { defaultValue: "Copy failed" }));
      }
    );
  }, [raw, shownRaw, t]);

  if (!raw) {
    return null;
  }

  const closeModal = () => {
    setOpen(false);
    setShowHiddenValues(false);
  };

  return (
    <BlockStack gap="100">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-slate-500">
          <Code className="w-4 h-4 text-indigo-500/80 shrink-0" />
          <span className="text-xs font-medium text-slate-500 select-none">
            {summary}
          </span>
        </div>
        <button
          onClick={() => setOpen(true)}
          className="inline-flex items-center gap-1 max-md:min-h-11 text-xs font-semibold text-indigo-600 hover:text-indigo-800 hover:underline transition duration-150 py-0.5 px-2 bg-indigo-50 hover:bg-indigo-100/60 rounded-md"
        >
          <Eye size={12} />
          {t("details", { defaultValue: "Details" })}
        </button>
      </div>

      <Modal
        open={open}
        onClose={closeModal}
        title={t("audit_changes_title", { defaultValue: "Change Data Details" })}
        primaryAction={{
          content: t("close", { defaultValue: "Close" }),
          onAction: closeModal,
        }}
      >
        <Modal.Section>
          <div className="flex flex-col gap-4">
            <div className="flex justify-between items-center bg-slate-50 p-2.5 rounded-xl border border-slate-100">
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider pl-1">
                {isBeforeAfterFormat ? t("diff_view", { defaultValue: "Field Comparisons" }) : t("raw_payload", { defaultValue: "JSON Payload" })}
              </span>
              <div className="flex items-center gap-2">
                {/* The comparison table has an eye per value; this switch is for the JSON text and «Copy JSON». */}
                {hasHiddenValues && !diffRows && (
                  <button
                    onClick={() => setShowHiddenValues(!showHiddenValues)}
                    className="inline-flex items-center gap-1.5 max-md:min-h-11 text-xs font-semibold px-3 py-1.5 bg-white border border-slate-200 hover:bg-slate-50 text-slate-600 hover:text-slate-800 rounded-lg shadow-sm transition duration-150"
                  >
                    {showHiddenValues ? <EyeOff size={14} /> : <Eye size={14} />}
                    {t(showHiddenValues ? "hide_hidden_values" : "show_hidden_values")}
                  </button>
                )}
                <button
                  onClick={onCopy}
                  className="inline-flex items-center gap-1.5 max-md:min-h-11 text-xs font-semibold px-3 py-1.5 bg-white border border-slate-200 hover:bg-slate-50 text-slate-600 hover:text-slate-800 rounded-lg shadow-sm transition duration-150"
                >
                  {copied ? <Check size={14} className="text-emerald-600" /> : <Copy size={14} />}
                  {copied ? t("copied", { defaultValue: "Copied" }) : t("copy", { defaultValue: "Copy JSON" })}
                </button>
              </div>
            </div>
            
            {diffRows ? (
              <div className="border border-slate-100 rounded-xl overflow-hidden shadow-sm">
                {diffRows.length === 0 && (
                  <div className="py-8 px-4 text-center">
                    <Text as="p" tone="subdued">
                      {t("no_changes_detected", { defaultValue: "No values were changed." })}
                    </Text>
                  </div>
                )}
                {diffRows.length > 0 && isPhone && (
                  // Three columns do not fit a phone: each change is a block with its two values stacked.
                  <div className="divide-y divide-slate-100">
                    {diffRows.map((row) => (
                      <div key={row.key} className="flex flex-col gap-1.5 p-3">
                        <span className="font-mono text-xs font-semibold text-slate-700">{row.key}</span>
                        <span className="text-xs text-slate-500">{t("before", { defaultValue: "Before" })}</span>
                        <ChangedValue row={row} side="before" />
                        <span className="text-xs text-slate-500">{t("after", { defaultValue: "After" })}</span>
                        <ChangedValue row={row} side="after" />
                      </div>
                    ))}
                  </div>
                )}
                {diffRows.length > 0 && !isPhone && (
                  <DataTable
                    columnContentTypes={["text", "text", "text"]}
                    headings={[
                      <span className="font-semibold text-slate-600 text-xs">{t("field", { defaultValue: "Field" })}</span>,
                      <span className="font-semibold text-slate-600 text-xs">{t("before", { defaultValue: "Before" })}</span>,
                      <span className="font-semibold text-slate-600 text-xs">{t("after", { defaultValue: "After" })}</span>
                    ]}
                    rows={diffRows.map((row) => [
                      <span className="font-mono text-xs font-semibold text-slate-700">{row.key}</span>,
                      <ChangedValue row={row} side="before" className="max-w-[280px]" />,
                      <ChangedValue row={row} side="after" className="max-w-[280px]" />,
                    ])}
                  />
                )}
              </div>
            ) : (
              <div className="bg-slate-950 text-slate-200 rounded-2xl p-4 font-mono text-xs shadow-inner border border-slate-850 overflow-auto max-h-[400px] leading-relaxed">
                <pre className="whitespace-pre-wrap word-break-all select-all selection:bg-indigo-500/30">
                  {shownRaw}
                </pre>
              </div>
            )}
          </div>
        </Modal.Section>
      </Modal>
    </BlockStack>
  );
}
